import { put, call, takeEvery, takeLatest } from "redux-saga/effects";
import * as actions from "./actions";
import * as constants from "./constants";
import { toast } from "react-toastify";
import * as service from "./service";

function* fetchUsersSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchUsers, action.params);
        yield put(actions.fetchUsersSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchUsersFailed(error));
        yield call(toast.error, "Erreur lors du chargement des utilisateurs.");
    }
}

function* createUserSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.createUser, action.payload);
        yield put(actions.createUserSuccess(response.data));
        yield call(toast.success, "Utilisateur créé avec succès.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.createUserFailed(error));
        const data = error?.response?.data;
        if (data?.email) {
            yield call(toast.error, data.email[0]);
        } else if (data?.phone) {
            yield call(toast.error, data.phone[0]);
        } else {
            yield call(toast.error, "Erreur lors de la création de l'utilisateur.");
        }
    }
}

function* updateUserSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.updateUser, action.id, action.payload);
        yield put(actions.updateUserSuccess(response.data));
        yield call(toast.success, "Utilisateur modifié avec succès.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.updateUserFailed(error));
        yield call(toast.error, "Erreur lors de la modification.");
    }
}

function* deleteUserSaga(action: any): Generator<any, void, any> {
    try {
        yield call(service.deleteUser, action.id);
        yield put(actions.deleteUserSuccess(action.id));
        yield call(toast.success, "Utilisateur désactivé avec succès.");
        if (action.callback) action.callback();
    } catch (error) {
        yield put(actions.deleteUserFailed(error));
        yield call(toast.error, "Erreur lors de la désactivation.");
    }
}

function* fetchRolesSaga(): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchRoles);
        yield put(actions.fetchRolesSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchRolesFailed(error));
    }
}

function* createRoleSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.createRole, action.payload);
        yield put(actions.createRoleSuccess(response.data));
        yield call(toast.success, "Rôle créé avec succès.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.createRoleFailed(error));
        const data = error?.response?.data;
        if (data?.name) {
            yield call(toast.error, data.name[0]);
        } else {
            yield call(toast.error, "Erreur lors de la création du rôle.");
        }
    }
}

function* updateRoleSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.updateRole, action.id, action.payload);
        yield put(actions.updateRoleSuccess(response.data));
        yield call(toast.success, "Permissions du rôle mises à jour.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.updateRoleFailed(error));
        yield call(toast.error, "Erreur lors de la mise à jour du rôle.");
    }
}

function* deleteRoleSaga(action: any): Generator<any, void, any> {
    try {
        yield call(service.deleteRole, action.id);
        yield put(actions.deleteRoleSuccess(action.id));
        yield call(toast.success, "Rôle supprimé.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.deleteRoleFailed(error));
        yield call(toast.error, "Erreur lors de la suppression du rôle.");
    }
}

function* suspendUserSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.suspendUser, action.id);
        yield put(actions.suspendUserSuccess(response.data));
        yield call(toast.success, "Compte suspendu avec succès.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.suspendUserFailed(error));
        yield call(toast.error, "Erreur lors de la suspension du compte.");
    }
}

function* activateUserSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.activateUser, action.id);
        yield put(actions.activateUserSuccess(response.data));
        yield call(toast.success, "Compte réactivé avec succès.");
        if (action.callback) action.callback();
    } catch (error: any) {
        yield put(actions.activateUserFailed(error));
        yield call(toast.error, "Erreur lors de la réactivation du compte.");
    }
}

function* fetchUserDetailSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.getUser, action.id);
        yield put(actions.fetchUserDetailSuccess(response.data));
    } catch (error: any) {
        yield put(actions.fetchUserDetailFailed(error));
        const data = error?.response?.data;
        if (data?.detail) {
            yield call(toast.error, data.detail);
        } else {
            yield call(toast.error, "Erreur lors du chargement de l'utilisateur.");
        }
    }
}

function* fetchUserStatsSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchUserStats, action.id);
        yield put(actions.fetchUserStatsSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchUserStatsFailed(error));
    }
}

function* fetchUserActiviteSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchUserActivite, action.id);
        yield put(actions.fetchUserActiviteSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchUserActiviteFailed(error));
    }
}

function* fetchUserDecisionsSaga(action: any): Generator<any, void, any> {
    try {
        const response = yield call(service.fetchUserDecisions, action.id);
        yield put(actions.fetchUserDecisionsSuccess(response.data));
    } catch (error) {
        yield put(actions.fetchUserDecisionsFailed(error));
    }
}

export default function* usersSaga() {
    yield takeEvery(constants.FETCH_USERS_REQUESTING, fetchUsersSaga);
    yield takeEvery(constants.CREATE_USER_REQUESTING, createUserSaga);
    yield takeEvery(constants.UPDATE_USER_REQUESTING, updateUserSaga);
    yield takeEvery(constants.DELETE_USER_REQUESTING, deleteUserSaga);
    yield takeEvery(constants.FETCH_ROLES_REQUESTING, fetchRolesSaga);
    yield takeEvery(constants.CREATE_ROLE_REQUESTING, createRoleSaga);
    yield takeEvery(constants.UPDATE_ROLE_REQUESTING, updateRoleSaga);
    yield takeEvery(constants.DELETE_ROLE_REQUESTING, deleteRoleSaga);
    yield takeEvery(constants.SUSPEND_USER_REQUESTING, suspendUserSaga);
    yield takeEvery(constants.ACTIVATE_USER_REQUESTING, activateUserSaga);
    yield takeLatest(constants.FETCH_USER_DETAIL_REQUESTING, fetchUserDetailSaga);
    yield takeLatest(constants.FETCH_USER_STATS_REQUESTING, fetchUserStatsSaga);
    yield takeLatest(constants.FETCH_USER_ACTIVITE_REQUESTING, fetchUserActiviteSaga);
    yield takeLatest(constants.FETCH_USER_DECISIONS_REQUESTING, fetchUserDecisionsSaga);
}
